import { Fragment, useCallback, useEffect, useMemo, useRef, useState, ReactNode } from "react";

import { ArrowPathIcon, MagnifyingGlassIcon, ChevronDownIcon, ChevronUpIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import { flexRender, getCoreRowModel, getExpandedRowModel, getPaginationRowModel, getSortedRowModel, RowModel, SortingState, Table, useReactTable } from "@tanstack/react-table";
import { useDebounce } from "@uidotdev/usehooks";
import { useTranslation } from "react-i18next";

import useApiHelper from "../../../hooks/useApiHelper";
import { PageMode } from "../../../models/PagedTable";
import { DefaultResponseWithData } from "../../../models/system";
import { ButtonInput } from "../Inputs/ButtonInput";
import ButtonGroup from "../ButtonGroup";
import LoadingWheel from "../LoadingWheel";
import apiConfig from "../../../utils/apiConfig";
import Pagination from "./Pagination";
import PaginationWithFilters, { IPaginationFilter } from "./PaginationWithFilters";
import SearchByFiltersModal, { SearchByOption } from "./SearchByFiltersModal";
import DateRangeFilter from "../DateRangeFilter";
import { DateRangeType } from "react-tailwindcss-datepicker";

export interface IPagedResponse<T> extends DefaultResponseWithData<IPagedData<T>> { }

export interface IPagedData<T> {
  rows: T[];
  total: number;
  totalPages: number;
  page: number;
  pageSize: number;
}

export interface IPagedTableActionButton<T> {
  label: string;
  onClick: (rows: RowModel<T>, table: Table<T>) => void;
  disabledOnNoRows?: boolean;
  isLoading?: boolean;
  groupedButtons?: IPagedTableActionButton<T>[];
}

export interface IPagedModeToggle {
  label: string;
  pageMode?: PageMode;
  urlQuery?: string;
}

export type OtherInfo = {
  label: string;
  value: string;
}

export interface IPagedTableProps<T> {
  url: string;
  columns: any;
  pageMode?: PageMode;
  queryLabel?: string;
  externalQuery?: string;
  modeToggles?: IPagedModeToggle[];
  customUrlQuery?: string;
  actionButtons?: IPagedTableActionButton<T>[];
  expandedRow?: (row: T) => JSX.Element;
  extraRow?: (row: T) => JSX.Element;
  onRowClick?: (row: T) => void;
  onRowDoubleClick?: (row: T) => void;
  showTotal?: boolean;
  additionalBody?: Record<string, any>;
  initialSort?: SortingState;
  setRowData?: (data: any) => void;
  rowColor?: (row: T) => string;
  otherInfo?: OtherInfo[];
  filters?: IPaginationFilter[];
  // Start on this chip instead of the default one - lets a link deep-link into a filtered view.
  initialFilterId?: string;
  itemLabel?: string; // e.g., "VenicOS" - defaults to "Rows"
  showDateFilter?: boolean;
  useButtonGroup?: boolean;
  buttonGroupLabel?: string;
  useSearchBy?: boolean;
  requireLoad?: boolean;
  dateFilterAddon?: ReactNode;
}

const EMPTY_ROWS: any[] = [];

export default function VehiclePagedTable<T>(tableConfig: IPagedTableProps<T>) {
  const { t } = useTranslation("common");
  const { post } = useApiHelper(apiConfig("PagedTable"));
  const [query, setQuery] = useState<string>("");
  const hasExternalQuery = tableConfig.externalQuery !== undefined;
  const [toggles, setToggles] = useState<boolean[]>(new Array(tableConfig.modeToggles ? tableConfig.modeToggles?.length : 0).fill(false));
  const [sorting, setSorting] = useState<SortingState>(tableConfig.initialSort ?? []);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [activeFilter, setActiveFilter] = useState<IPaginationFilter | null>(
    (tableConfig.initialFilterId ? tableConfig.filters?.find(f => f.id === tableConfig.initialFilterId) : undefined)
    || tableConfig.filters?.find(f => f.default)
    || null
  );
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const abortControllerRef = useRef<AbortController | null>(null);
  const [groupedButtons, setGroupedButtons] = useState<IPagedTableActionButton<T>[] | null>(null);
  const [dateFieldType, setDateFieldType] = useState<string>("Created");
  const [dates, setDates] = useState<DateRangeType>({
    startDate: null,
    endDate: null
  });
  const [isLoadEnabled, setIsLoadEnabled] = useState(!tableConfig.requireLoad);
  const initialAdditionalBodyRef = useRef(JSON.stringify(tableConfig.additionalBody));

  const searchByOptions: SearchByOption[] = [
    // adam ones
    { label: "Vrm", value: "vrm" },
    { label: "Full Name", value: "contactName" },
    { label: "Phone Number", value: "phoneNumber" },
    { label: "Driver Name", value: "driverName" },
    // old ones
    { label: "Stock Number", value: "stockNumber" },
    { label: "Make", value: "make" },
    { label: "Model", value: "model" },
    { label: "Year", value: "year" },
    { label: "Engine Code", value: "engineCode" },
    { label: "Yard Location", value: "yardLocation" },
  ];

  const [searchBy, setSearchBy] = useState<string[]>([
    "vrm",
    "contactName",
    "phoneNumber",
    "driverName",
    "stockNumber",
    "make",
    "model",
    "year",
    "engineCode",
    "yardLocation"
  ]);


  const { setRowData } = tableConfig;

  const debouncedQuery = useDebounce(query, 1000);

  const effectiveActionButtons = useMemo<IPagedTableActionButton<T>[] | undefined>(() => {
    if (!tableConfig.useButtonGroup) return tableConfig.actionButtons;
    if (!tableConfig.actionButtons || tableConfig.actionButtons.length === 0) return tableConfig.actionButtons;

    return [{
      label: tableConfig.buttonGroupLabel ?? t("Bulk Actions"),
      onClick: () => { },
      groupedButtons: tableConfig.actionButtons,
    }];
  }, [tableConfig.actionButtons, tableConfig.useButtonGroup, tableConfig.buttonGroupLabel, t]);

  // Merge filter-based additionalBody with tableConfig.additionalBody
  const mergedAdditionalBody = useMemo(() => {
    console.log("ActiveFilter: ", activeFilter);

    var searchFields = {
      ...searchBy.map(field => ({ [field]: true })).reduce((acc, curr) => ({ ...acc, ...curr }), {})
    };

    const baseBody = tableConfig.additionalBody || {};
    if (activeFilter && activeFilter.type === "additionalBody" && activeFilter.additionalBodyKey) {
      return {
        ...baseBody,
        [activeFilter.additionalBodyKey]: activeFilter.value,
        searchFields: searchFields,
        ...dates,
        dateField: dateFieldType
      };
    }
    if (activeFilter && activeFilter.type === "searchBody") {
      return {
        ...baseBody,
        ...activeFilter.value,
        searchFields: searchFields,
        ...dates,
        dateField: dateFieldType
      };
    }

    return baseBody;
  }, [tableConfig.additionalBody, activeFilter, searchBy, dates]);

  const debouncedAdditionalBody = useDebounce(mergedAdditionalBody, 300);
  const pageParams = [debouncedQuery, pageIndex, toggles, sorting, pageSize, activeFilter];

  const getPageMode = useCallback(() => {
    // Check if active filter is a pageMode filter
    if (activeFilter && activeFilter.type === "pageMode") {
      return activeFilter.value as PageMode;
    }

    const activeModes = tableConfig.modeToggles
      ?.filter((toggle, i) => toggle.pageMode && toggles[i])
      .map(toggle => toggle.pageMode);

    // If no modes active, return default; otherwise a joined string of all modes
    return !activeModes || activeModes.length === 0
      ? tableConfig.pageMode
      : activeModes.join(',');
  }, [tableConfig, toggles, activeFilter]);

  const currentPageMode = useMemo(() => getPageMode(), [getPageMode]);

  // Cleanup function to cancel pending requests
  const cleanup = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  useEffect(() => {
    if (!hasExternalQuery) return;

    const external = tableConfig.externalQuery ?? "";
    if (external !== query) {
      setQuery(external);
      setPageIndex(1);
    }
  }, [hasExternalQuery, tableConfig.externalQuery, query]);


  const getUrl = useCallback(() => {
    const { url, customUrlQuery, modeToggles } = tableConfig;

    const queryString =
      modeToggles
        ?.filter(toggle => toggle.urlQuery)
        .map(toggle => toggle.urlQuery)
        .join("&") ?? "";

    const customQuery = customUrlQuery ? `&${customUrlQuery}` : "";
    const modeQuery = currentPageMode;

    let finalUrl = `${url}/${pageIndex}?pageMode=${modeQuery}`;

    if (pageSize) finalUrl += `&pageSize=${pageSize}`;

    // Add the queries if they exist
    if (debouncedQuery.trim()) finalUrl += `&query=${debouncedQuery}`;

    if (queryString) finalUrl += `&${queryString}`;

    // Append the custom query if provided
    finalUrl += customQuery;

    return finalUrl;
  }, [tableConfig, debouncedQuery, pageIndex, getPageMode, pageSize]);

  const getSort = useCallback(() => {
    let sort: any = {};

    if (sorting.length != 0) {
      let matchingCol = tableConfig.columns.filter((x: any) => x.id == sorting[0]?.id)[0];
      let key = matchingCol?.accessorKey ? matchingCol.accessorKey : sorting[0].id;

      sort = { ...sorting[0], id: key };
    }

    return sort;
  }, [sorting, tableConfig.columns]);


  const requestBody = useMemo(() => {
    return {
      ...getSort(),
      searchBody: {
        ...debouncedAdditionalBody
      }
    };
  }, [sorting, tableConfig.columns, debouncedAdditionalBody]);

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ["pagedTable", tableConfig.url, currentPageMode, debouncedAdditionalBody, activeFilter, ...pageParams],
    ({ signal }) => {
      // Cancel any previous request
      cleanup();

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      return post<any>(getUrl(), requestBody, abortControllerRef.current.signal) as Promise<IPagedResponse<T>>;
    },
    {
      enabled: isLoadEnabled,
      keepPreviousData: true,
      staleTime: 1000 * 60 * 2, // 2 minutes
      refetchInterval: 1000 * 60 * 5, // 5 minutes
      retry: 1, // Limit retries to prevent infinite loops
      retryDelay: 1000, // Wait 1 second before retrying
      onError: (error) => {
        console.error('PagedTable query error:', error);
      },
    },
  );

  useEffect(() => {
    if (data?.data.rows && setRowData) setRowData(data.data.rows);
  }, [data?.data.rows]);

  useEffect(() => {
    if (!tableConfig.requireLoad || isLoadEnabled) return;
    if (debouncedQuery.trim()) setIsLoadEnabled(true);
  }, [debouncedQuery]);

  useEffect(() => {
    if (!tableConfig.requireLoad || isLoadEnabled) return;
    if (JSON.stringify(debouncedAdditionalBody) !== initialAdditionalBodyRef.current) setIsLoadEnabled(true);
  }, [debouncedAdditionalBody]);

  // Drop any column that exists purely to host the expand chevron when this table
  // has no panel to expand, so it doesn't leave an empty column in the layout.
  const columns = useMemo(
    () => tableConfig.expandedRow
      ? tableConfig.columns
      : (tableConfig.columns as any[]).filter((col) => !col?.meta?.chevronOnly),
    [tableConfig.columns, tableConfig.expandedRow]
  );

  const table = useReactTable({
    data: data?.data.rows ?? EMPTY_ROWS,
    columns: columns,
    state: {
      sorting: sorting,
    },
    enableRowSelection: true,
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // Lets the shared checkbox column hide its expand chevron when this table has
    // no panel to show - otherwise the chevron expands an empty row.
    meta: { hasExpandedRow: !!tableConfig.expandedRow },
  });
  const selectedRowCount = table.getSelectedRowModel().rows.length;
  const prevSelectedRowCountRef = useRef(0);

  useEffect(() => {
    const justSelectedRows = prevSelectedRowCountRef.current === 0 && selectedRowCount > 0;
    const selectionCleared = prevSelectedRowCountRef.current > 0 && selectedRowCount === 0;
    const firstGroupedAction = effectiveActionButtons?.find(
      (button) => button.groupedButtons && button.groupedButtons.length > 0
    );

    if (justSelectedRows && !groupedButtons && firstGroupedAction?.groupedButtons) {
      setGroupedButtons(firstGroupedAction.groupedButtons);
    }

    if (selectionCleared && groupedButtons) {
      setGroupedButtons(null);
    }

    prevSelectedRowCountRef.current = selectedRowCount;
  }, [selectedRowCount, effectiveActionButtons, groupedButtons]);

  useEffect(() => {
    // Whenever data rows change, clear selection for any deleted rows
    if (table.getState().rowSelection && data?.data?.rows) {
      const validRowIds = new Set(data.data.rows.map((_, i) => i.toString()));
      const currentSelection = table.getState().rowSelection;

      // If any selected IDs are no longer valid, reset
      const hasInvalid = Object.keys(currentSelection).some(
        (key) => !validRowIds.has(key)
      );
      if (hasInvalid) {
        table.resetRowSelection();
      }
    }
  }, [data?.data?.rows, table]);

  useEffect(() => {
    if (data && !isLoading) {
      table.setPageSize(data.data.pageSize);
    }
  }, [data?.data?.pageSize, isLoading]);

  // useEffect(() => {
  //   console.log("Sorting changed:", getSort());
  // }, [sorting]);

  const changeQuery = (newQuery: string) => {
    if (isLoading) return;
    setQuery(newQuery);
    setPageIndex(1);
  };

  const changePage = (increment: number) => {
    if (isLoading) return;
    setPageIndex(pageIndex + increment);
  }

  const handlePageChange = (page: number) => {
    if (isLoading) return;
    setPageIndex(page);
  }

  const handlePageSizeChange = (size: number) => {
    if (isLoading) return;
    setPageSize(size);
    setPageIndex(1); // Reset to first page when changing page size
  }

  const handleFilterChange = (filter: IPaginationFilter | null) => {
    if (isLoading) return;
    setActiveFilter(filter);
    setPageIndex(1); // Reset to first page when changing filter
  }

  const handleToggle = (index: number) => {
    if (isLoading) return;

    let updatedToggles = [...toggles];

    // Flip the toggle state, allowing multiple to be active at once
    updatedToggles[index] = !updatedToggles[index];

    setToggles(updatedToggles);
  }

  // useEffect(() => {
  //   let rows = table.getRowModel();
  // }, [table.getSelectedRowModel()]);

  const lastUrlRef = useRef<string | null>(null);
  const lastBodyRef = useRef<string | null>(null);

  const memoizedUrl = useMemo(() => getUrl(), [getUrl]);
  const memoizedBody = useMemo(() => JSON.stringify(requestBody), [requestBody]);

  useEffect(() => {
    if (data && !isLoading && !isFetching) {
      lastUrlRef.current = memoizedUrl;
      lastBodyRef.current = memoizedBody;
    }
  }, [data, isLoading, isFetching, memoizedUrl, memoizedBody]);

  // Determine if we should show loading
  const shouldShowLoading = isLoading ||
    (isFetching && lastUrlRef.current !== memoizedUrl) ||
    (isFetching && lastBodyRef.current !== memoizedBody);


  // Determine if we should show error state
  const shouldShowError = error && !data?.data?.rows?.length && !isLoading;

  // Check if error is an abort error (which is expected during cleanup)
  const isAbortError = error && (
    (error as any)?.name === 'AbortError' ||
    (error as any)?.message?.includes('aborted')
  );

  // Show error state only if we have a real error and no previous data
  if (shouldShowError && !isAbortError) {
    return (
      <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-red-800">{t("Error loading data. Please try again.")}</p>
        <ButtonInput
          label={t("Retry")}
          onClick={() => refetch()}
          classes="mt-2"
        />
      </div>
    );
  }

  const searchPlaceholder = tableConfig.useSearchBy && searchBy.length > 0
    ? t("Search by {{fields}}", { fields: searchBy.map(s => t(s.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()))).join(', ').replace(/, ([^,]*)$/, ` ${t("and")} $1`) })
    : tableConfig.queryLabel ?? t("Search...");


  return (
    <div className="space-y-4">
      <PaginationWithFilters
        currentPage={data?.data.page ?? pageIndex}
        totalPages={data?.data.totalPages ?? 1}
        pageSize={pageSize}
        totalRows={data?.data.total ?? 0}
        itemLabel={tableConfig.itemLabel}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        filters={tableConfig.filters}
        activeFilter={activeFilter ?? null}
        onFilterChange={handleFilterChange}
        isLoading={shouldShowLoading}
      />

      <div className="flex justify-between">

        <div className="flex gap-x-5 items-center">
          {effectiveActionButtons && effectiveActionButtons.length > 0 && (
            <div className="flex gap-4 flex-wrap">
              {effectiveActionButtons?.map((button, i) => {
                const isExpanded = expandedGroups.has(i);
                const hasGroupedButtons = button.groupedButtons && button.groupedButtons.length > 0;

                if (hasGroupedButtons) {
                  return (
                    <div
                      key={"action_" + i}
                      className="relative"
                      onClick={() => {
                        // const newExpanded = new Set(expandedGroups);
                        // newExpanded.add(i);
                        // setExpandedGroups(newExpanded);
                        setGroupedButtons(groupedButtons ? null : button.groupedButtons!);
                      }}

                    >
                      <ButtonInput
                        label=""
                        disabled={button.disabledOnNoRows ? table.getSelectedRowModel().rows.length === 0 : false}
                        isSubmit={false}
                        onClick={() => { }} // Empty onClick to prevent any action
                        classes="px-3 py-2 shadow-sm bg-primary-600 hover:bg-primary-700 disabled:bg-gray-500 disabled:hover:bg-gray-500 !text-white flex items-center gap-2"
                      >
                        <span className="flex items-center gap-2 !text-white">
                          {button.label}
                          {groupedButtons == button.groupedButtons ? (
                            <ChevronUpIcon className="h-4 w-4" />
                          ) : (
                            <ChevronDownIcon className="h-4 w-4" />
                          )}
                        </span>
                      </ButtonInput>
                    </div>
                  );
                }

                return button.isLoading ? (
                  <ButtonInput key={i} disabled={true} isSubmit={false} onClick={() => button.onClick(table.getSelectedRowModel(), table)} classes={"!bg-primary-600 px-3 py-2 text-white"}>
                    <ArrowPathIcon className="animate-spin bg-primary-600 h-5 w-5" />
                  </ButtonInput>
                ) : (
                  <ButtonInput key={i} label={button.label} disabled={button.disabledOnNoRows ? table.getSelectedRowModel().rows.length === 0 : false} isSubmit={false} onClick={() => button.onClick(table.getSelectedRowModel(), table)} classes={""} />
                );
              })}
            </div>
          )}

          {tableConfig.queryLabel ? (
            <>
              {tableConfig.useSearchBy && (
                <SearchByFiltersModal
                  options={searchByOptions}
                  searchBy={searchBy}
                  setSearchBy={setSearchBy}
                  disabled={isLoading}
                />
              )}

              <div className="relative flex items-stretch flex-grow focus-within:z-10">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </div>
                <input
                  type="text"
                  disabled={isLoading}
                  onChange={(e) => changeQuery(e.target.value)}
                  className="pl-10 border-gray-300 rounded-lg sm:text-sm lg:min-w-[250px] sm:min-w-[unset] max-w-full lg:max-w-[calc(100% - 3rem)]"
                  // add space where camel case
                  // join with comma except second to last with 'and'
                  placeholder={searchPlaceholder}
                  style={{ width: `calc(${searchPlaceholder.length}ch - 2rem)` }}
                />
              </div>
            </>
          ) : null}

          {tableConfig.showDateFilter !== false && (
            <DateRangeFilter
              dates={dates}
              onDatesChange={(newDates: DateRangeType) => {
                console.log("DateRangeFilter newDates:", newDates);
                setDates(newDates);
              }}
              dateFieldType={dateFieldType}
              onDateFieldTypeChange={setDateFieldType}
              showLabels={false}
              options={[
                { label: "Created", value: "Created" },
                { label: "Scheduled On", value: "scheduledCollectionOn" },
                { label: "Collected On", value: "collectedOn" }
              ]}
            />
          )}

          {tableConfig.dateFilterAddon}

          {toggles?.map((toggle, i) =>
            <div className="flex items-center" key={"toggle_" + i}>
              <input onChange={() => handleToggle(i)} checked={toggle} type="checkbox" className="mr-2 h-5 w-5" />
              <label >{tableConfig.modeToggles![i].label}</label>
            </div>
          )}

        </div>

        {/* {tableConfig.showTotal &&
          <div className="flex items-center gap-2">
            <div className="text-sm">Total Rows: {data?.data.total}</div>
          </div>
        } */}

        <div className="flex items-end gap-2 flex-col">
          {tableConfig.showTotal && <div className="text-sm">{t("Total Rows:")} {data?.data.total}</div>}
          {tableConfig.otherInfo && tableConfig.otherInfo.length > 0 && tableConfig.otherInfo.map((info, i) =>
            <div className="text-sm" key={"info_" + i}>{info?.label}: {(data?.data as any)?.[info?.value]}</div>
          )}
        </div>

      </div>

      {groupedButtons && (
        // <div className="absolute top-full left-0 mt-1 bg-white z-10 min-w-full">
        <ButtonGroup
          options={groupedButtons.map((groupedButton, index) => ({
            id: index.toString(),
            label: groupedButton.label,
            value: groupedButton.label, // Not used in action mode, but required by interface
            disabled: groupedButton.disabledOnNoRows ? table.getSelectedRowModel().rows.length === 0 : false,
            onClick: () => groupedButton.onClick(table.getSelectedRowModel(), table),
            isLoading: groupedButton.isLoading,
            customContent: groupedButton.isLoading ? (
              <ArrowPathIcon className="animate-spin h-5 w-5" />
            ) : undefined,
          }))}
          actionMode={true}
          size="md"
          sticky
        />
        // </div>
      )}

      <div className="bg-white overflow-x-auto overflow-y-hidden">
        <table className="min-w-full overflow-scroll divide-y !rounded-lg border divide-gray-300">
          <thead>
            {table.getHeaderGroups().map((headerGroup, i) => (
              <tr key={"header_group_" + headerGroup.id + "_" + i} className="bg-primary-50 text-gray-800">
                {headerGroup.headers.map((header, i) => (
                  <th
                    key={"header_" + header.id + "_" + i}
                    scope="col"
                    className={`py-3.5 px-3 text-sm font-semibold lg:table-cell text-left ${(header.column.columnDef.meta as any)?.thClassName ?? ""}`}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={
                          header.column.getCanSort()
                            ? "cursor-pointer select-none"
                            : ""
                        }
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: " ▲",
                          desc: " ▼",
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          {/* Deferred load placeholder */}
          {!isLoadEnabled && (
            <tbody>
              <tr>
                <td colSpan={12}>
                  <div className="flex flex-col items-center justify-center py-16 gap-3 text-gray-400">
                    <TableCellsIcon className="h-10 w-10 text-gray-300" />
                    <p className="text-sm text-gray-500">{t("Search above to find specific stock, or load the full list.")}</p>
                    <button
                      type="button"
                      onClick={() => setIsLoadEnabled(true)}
                      className="mt-1 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                      {t("Load all stock")}
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          )}

          {/* Loading state keeps its own tbody */}
          {isLoadEnabled && shouldShowLoading && (
            <tbody>
              <tr>
                <td colSpan={12}>
                  <div className="flex justify-center py-4">
                    <LoadingWheel />
                  </div>
                </td>
              </tr>
            </tbody>
          )}

          {/* Empty state */}
          {isLoadEnabled && !shouldShowLoading && table.getRowModel().rows.length === 0 && (
            <tbody>
              <tr>
                <td colSpan={12}>
                  <div className="flex justify-center py-8 text-sm text-gray-500">
                    {t("No rows to display.")}
                  </div>
                </td>
              </tr>
            </tbody>
          )}

          {/* Actual rows */}
          {isLoadEnabled && !shouldShowLoading &&
            table.getRowModel().rows.map((row) => {
              const rowCells = row.getVisibleCells();
              const rowKey = (row.original as any)?.id || row.id;

              return (
                <tbody key={"row" + rowKey} className="group">

                  {/* Extra row */}
                  {tableConfig.extraRow && (
                    <tr
                      onClick={() => tableConfig.onRowClick?.(row.original)}
                      className={`group-hover:bg-gray-100 ${tableConfig.onRowClick ? "hover:cursor-pointer" : ""} ${tableConfig.rowColor?.(row.original) ?? ""
                        }`}
                    >

                      <td colSpan={rowCells.length}>
                        {tableConfig.extraRow(row.original)}
                      </td>
                    </tr>
                  )}

                  {/* Main row */}
                  <tr
                    onClick={() => tableConfig.onRowClick?.(row.original)}
                    onDoubleClick={() => tableConfig.onRowDoubleClick?.(row.original)}
                    className={`group-hover:bg-gray-100 ${tableConfig.onRowClick || tableConfig.onRowDoubleClick ? "hover:cursor-pointer" : ""} !${tableConfig.rowColor?.(row.original) ?? ""
                      }`}
                  >
                    {rowCells.map((cell, i) => (
                      <td key={"cell_" + cell.id + "_" + i} className={`px-3 py-3 border text-sm ${(cell.column.columnDef.meta as any)?.tdClassName ?? ""}`}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>

                  {/* Expanded row */}
                  {row.getIsExpanded() && tableConfig.expandedRow && (
                    <tr className="group-hover:bg-gray-100">
                      <td colSpan={rowCells.length}>
                        {tableConfig.expandedRow?.(row.original)}
                      </td>
                    </tr>
                  )}
                </tbody>
              );
            })}
        </table>
      </div>

      {isLoadEnabled && !shouldShowLoading && table.getRowModel().rows.length > 0 && (
        <PaginationWithFilters
          currentPage={data?.data.page ?? pageIndex}
          totalPages={data?.data.totalPages ?? 1}
          pageSize={pageSize}
          totalRows={data?.data.total ?? 0}
          itemLabel={tableConfig.itemLabel}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          filters={tableConfig.filters}
          activeFilter={activeFilter ?? null}
          onFilterChange={handleFilterChange}
          isLoading={shouldShowLoading}
        />
      )}
    </div>
  );
}